import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {observer} from "mobx-react";
import {Badge, Button, Card, Col, Container, Form, Row, Spinner, Alert} from "react-bootstrap";
import HeroSection from "../../../components/HeroSection.jsx";
import {AgGridReact} from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {productApi} from "../../../api/product.js";

function AdminProductsPage() {
    const gridRef = useRef(null);
    const [gridApi, setGridApi] = useState(null);
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [quickFilter, setQuickFilter] = useState("");
    const [taxAuthorityFilter, setTaxAuthorityFilter] = useState("");
    const [productTypeFilter, setProductTypeFilter] = useState("");
    const [deliveryModeFilter, setDeliveryModeFilter] = useState("");

    const columnDefs = useMemo(() => ([
        {headerName: "Product", field: "name", filter: "agTextColumnFilter"},
        {headerName: "Code", field: "productCode", filter: "agTextColumnFilter", maxWidth: 160},
        {headerName: "Type", field: "productType", filter: "agTextColumnFilter", maxWidth: 140},
        {headerName: "Tax Authority", field: "taxAuthorityName", filter: "agTextColumnFilter"},
        {headerName: "Delivery", field: "deliveryMode", filter: "agTextColumnFilter", maxWidth: 140},
        {headerName: "Online", field: "online", filter: "agSetColumnFilter", maxWidth: 110, valueFormatter: (p) => p.value ? "Yes" : "No"},
        {headerName: "Billable", field: "billable", filter: "agSetColumnFilter", maxWidth: 110, valueFormatter: (p) => p.value ? "Yes" : "No"},
        {headerName: "Actions", field: "id", cellRenderer: "actionRenderer", filter: false, sortable: false, maxWidth: 120}
    ]), []);

    const defaultColDef = useMemo(() => ({
        flex: 1,
        minWidth: 140,
        sortable: true,
        filter: true,
        floatingFilter: true,
        resizable: true
    }), []);

    const loadProducts = useCallback(async () => {
        setIsLoading(true);
        try {
            const products = await productApi.getProducts();
            const normalized = products.map((p) => ({
                ...p,
                productType: p.productType || "",
                deliveryMode: p.deliveryMode || ""
            }));
            setRowData(normalized);
            setError("");
        } catch (e) {
            console.error("Failed to load products", e);
            setError("Failed to load products. Please try again.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProducts();
    }, [loadProducts]);

    useEffect(() => {
        if (!gridApi) return;
        gridApi.setQuickFilter(quickFilter);
        const managed = ["taxAuthorityName", "productType", "deliveryMode"];
        const nextModel = {...(gridApi.getFilterModel() || {})};
        managed.forEach((c) => delete nextModel[c]);
        if (taxAuthorityFilter) nextModel.taxAuthorityName = {filterType: "text", type: "contains", filter: taxAuthorityFilter};
        if (productTypeFilter) nextModel.productType = {filterType: "text", type: "equals", filter: productTypeFilter};
        if (deliveryModeFilter) nextModel.deliveryMode = {filterType: "text", type: "equals", filter: deliveryModeFilter};
        gridApi.setFilterModel(nextModel);
        gridApi.onFilterChanged();
    }, [gridApi, quickFilter, taxAuthorityFilter, productTypeFilter, deliveryModeFilter]);

    const handleClearFilters = () => {
        setQuickFilter("");
        setTaxAuthorityFilter("");
        setProductTypeFilter("");
        setDeliveryModeFilter("");
        if (gridApi) {
            gridApi.setFilterModel({});
            gridApi.setQuickFilter("");
            gridApi.onFilterChanged();
        }
    };

    const onGridReady = useCallback((params) => {
        setGridApi(params.api);
    }, []);

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="admin"
                eyebrow="Admin"
                title="Products"
                subtitle="Manage products across all tax authorities."
                actions={<Button variant="primary" disabled>Add Product</Button>}
            />
            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            <Card className="dashboard-panel mt-4">
                <Card.Header className="bg-white"><strong>Filters</strong></Card.Header>
                <Card.Body>
                    <Form>
                        <Row className="g-3 align-items-end">
                            <Col md={4}>
                                <Form.Label>Quick Search</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={quickFilter}
                                    placeholder="Search across all columns"
                                    onChange={(e) => setQuickFilter(e.target.value)}
                                />
                            </Col>
                            <Col md={3}>
                                <Form.Label>Tax Authority</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={taxAuthorityFilter}
                                    placeholder="Filter by tax authority"
                                    onChange={(e) => setTaxAuthorityFilter(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label>Product Type</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={productTypeFilter}
                                    placeholder="TaxCertificate..."
                                    onChange={(e) => setProductTypeFilter(e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label>Delivery</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={deliveryModeFilter}
                                    placeholder="Online/Manual"
                                    onChange={(e) => setDeliveryModeFilter(e.target.value)}
                                />
                            </Col>
                            <Col md="auto">
                                <Button variant="outline-secondary" onClick={handleClearFilters}>
                                    Clear Filters
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
            <Card className="dashboard-panel mt-4">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong>All Products</strong>
                    <Badge bg="light" text="dark">{rowData.length}</Badge>
                </Card.Header>
                <Card.Body>
                    {isLoading ? (
                        <div className="d-flex align-items-center">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span>Loading products...</span>
                        </div>
                    ) : (
                        <div className="ag-theme-alpine dashboard-grid" style={{height: "600px", width: "100%"}}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={rowData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination={true}
                                paginationAutoPageSize={true}
                                animateRows={true}
                                onGridReady={onGridReady}
                                components={{actionRenderer: ActionRenderer}}
                            />
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
}

function ActionRenderer(props) {
    return (
        <div style={{width: "100%", textAlign: "center"}}>
            <a href={`/admin/products/${props.data.id}`}><i style={{fontSize: 18}} className="bi bi-search"></i></a>
        </div>
    );
}

export default observer(AdminProductsPage);
